// *** Service for the EndpointBuilder component structure ***
// --- see the EndpointBuilder component for an example of how it works ---

// root structure of the EndpointBuilder component, containing titles
export interface IEndpointBuilderRoot {
  titles: IEndpointBuilderTitle[]; // list of childs (title)
  defaults?: IEndpointBuilderDefault[]; // default values - like chips but invisible and always selected
  isVisible?: () => boolean; // compute node visibility
  // --- private ---
  visible?: boolean; // is node visible
}

// title, containing subtitles
export interface IEndpointBuilderTitle {
  title: string; // title
  description?: string; // description
  subtitles: IEndpointBuilderSubtitle[]; // list of childs (subtitle)
  isVisible?: () => boolean; // compute node visibility
  // --- private ---
  visible?: boolean; // is node visible
}

// subtitle, containing chips
export interface IEndpointBuilderSubtitle {
  subtitle: string; // title
  description?: string; // description
  chips: IEndpointBuilderChip[]; // list of child (chip)
  selectType: SelectType; // category select type => SINGLE or MULTIPLE
  ignore?: boolean; // don't display subtitle
  isVisible?: () => boolean; // compute node visibility
  // --- private ---
  visible?: boolean; // is node visible
}

// chip
export interface IEndpointBuilderChip {
  text: string; // title
  parameterName?: string; // endpoint parameter name
  parameterValue?: string; // endpoint parameter value
  parameterDeprecatedNames?: string[]; // backward name compatibility
  softParameterValue?: boolean; // if set to true, the chip will activate even if the parameter value at the init. endpoint differ - skip parameter value verification
  isVisible?: () => boolean; // compute node visibility
  isActive?: () => boolean; // compute if chip is active
  isSelected?: () => boolean; // compute if chip is selected - only executed during endpoint set
  doForceParameter?: () => boolean; // force parameter insertion even if chip is invisible, unselected or inactive
  makeParameterValue?: () => string; // compute parameter value
  clickHandle?: (isChipActive: boolean) => void; // called each time the chip is called
  // --- private ---
  selected?: boolean; // is this chip selected
  visible?: boolean; // is node visible
  active?: boolean; // is chip active
  forceParameter?: boolean; // is parameter forced - see doForceParameter
}

// default
export interface IEndpointBuilderDefault {
  parameterName: string; // endpoint parameter name
  parameterValue?: string; // endpoint parameter value
  parameterDeprecatedNames?: string[]; // backward name compatibility
  makeParameterValue?: () => string; // compute parameter value
  isActive?: () => boolean; // compute if chip is active
  setByParams?: boolean; // Use params to set this field
  // --- private ---
  active?: boolean; // is chip active
}

// category selection type
export enum SelectType {
  SINGLE,
  MULTIPLE,
}

// service for the endpoint builder structure, needed in the EndpointBuilder props
export class EndpointBuilderService {
  // class variables
  structure: IEndpointBuilderRoot;
  endpoint: string = '';

  constructor() {
    this.structure = { titles: [], isVisible: undefined }; // default structure
  }

  refreshHandler: () => void = () => {
    return;
  };
  endpointChangeHandler: (newEndpoint: string) => void = () => {
    return;
  };

  // defines the base endpoint for this endpoint builder service
  setEndpoint(endpoint: string) {
    this.endpoint = endpoint;
    this.parseEndpoint();
  }

  // defines a new structure for the endpoint builder
  setStructure(newStructure: IEndpointBuilderRoot) {
    this.structure = newStructure;
    this.computeAllNodes();
  }

  // the service will call this function when its value are changed
  // it's needed to refresh the service in the state in the parent
  // refreshing the service in the parent will refresh the EndpointBuilder component
  setServiceRefreshHandler(refreshHandler: () => void) {
    this.refreshHandler = refreshHandler;
  }

  // the service will call this function each time the endpoint is changed
  setEndpointChangeHandler(endpointChangeHandler: (newEndpoint: string) => void) {
    this.endpointChangeHandler = endpointChangeHandler;
  }

  // gets structure root
  getRoot(): IEndpointBuilderRoot {
    return this.structure;
  }

  // gets a title
  getTitle(title: string): IEndpointBuilderTitle | undefined {
    return this.structure.titles.find((t: IEndpointBuilderTitle) => t.title === title);
  }

  // gets a subtitle
  getSubtitle(title: string, subtitle: string): IEndpointBuilderSubtitle | undefined {
    // get title
    const titleObj = this.getTitle(title);
    if (titleObj === undefined) {
      return undefined;
    }
    // get subtitle
    return titleObj.subtitles.find((s: IEndpointBuilderSubtitle) => s.subtitle === subtitle);
  }

  // gets a chip
  getChip(title: string, subtitle: string, chipText: string): IEndpointBuilderChip | undefined {
    // get subtitle
    const subtitleObj = this.getSubtitle(title, subtitle);
    if (subtitleObj === undefined) {
      return undefined;
    }
    // get chip
    return subtitleObj.chips.find((s: IEndpointBuilderChip) => s.text === chipText);
  }

  // is chip selected
  // return true if any of the chip is selected
  // return false if not selected
  isOneOfChipSelected(title: string, subtitle: string, chipText: string[]): boolean {
    for (const cName of chipText) {
      const ch = this.getChip(title, subtitle, cName);
      if (ch !== undefined && ch.selected !== undefined && ch.selected) {
        return true;
      }
    }
    return false;
  }

  // Gets default
  getDefault(parameterName: string): IEndpointBuilderDefault | undefined {
    return (this.structure.defaults || []).find(
      (t: IEndpointBuilderDefault) => t.parameterName === parameterName,
    );
  }

  // handle click event on a chip
  handleChipClick(subtitle: IEndpointBuilderSubtitle, chip: IEndpointBuilderChip): void {
    const newChipValue: boolean = !chip.selected;
    // handle single-select subtitle mode by delelecting all chips in the subtitle before selecting this one
    if (subtitle.selectType === SelectType.SINGLE) {
      subtitle.chips.forEach((c: IEndpointBuilderChip) => {
        c.selected = false;
      });
    }
    // set chip state
    chip.selected = newChipValue;
    // call chip click handle function, if any
    if (chip.clickHandle) {
      chip.clickHandle(chip.selected);
    }
    // refresh all
    this.refresh();
  }

  refresh = () => {
    // recompute nodes - like isActive or isVisible
    this.computeAllNodes();
    // update endpoint-URL
    this.updateEndpointURL();
    // refresh component
    if (this.refreshHandler) {
      this.refreshHandler();
    }
  };

  // call all node handlers
  computeAllNodes = (isInit?: boolean): void => {
    this.structure.titles.forEach((title: IEndpointBuilderTitle) => {
      // ------ title ------
      if (title.isVisible) {
        title.visible = title.isVisible();
      }
      title.subtitles.forEach((subtitle: IEndpointBuilderSubtitle) => {
        // ------ subtitle ------
        if (subtitle.isVisible) {
          subtitle.visible = subtitle.isVisible();
        }
        subtitle.chips.forEach((chip: IEndpointBuilderChip) => {
          // ------ chip ------
          // compute visible
          if (chip.isVisible) {
            chip.visible = chip.isVisible();
          }
          // compute active
          if (chip.isActive) {
            chip.active = chip.isActive();
          }
          // compute selected
          if (isInit && chip.isSelected) {
            // only executed at endpoint-init
            chip.selected = chip.isSelected();
          }
          // compute parameter-value
          if (chip.makeParameterValue) {
            chip.parameterValue = chip.makeParameterValue();
          }
          // compute force-parameter
          if (chip.doForceParameter) {
            chip.forceParameter = chip.doForceParameter();
          }
        });
      });
    });

    // update defaults
    (this.structure.defaults || []).forEach((def: IEndpointBuilderDefault) => {
      if (def.isActive) {
        def.active = def.isActive();
      }
      if (def.makeParameterValue) {
        def.parameterValue = def.makeParameterValue();
      }
    });
  };

  // update endpoint URL
  updateEndpointURL(): void {
    const endpointBase: string = this.endpoint.split('?')[0]; // remove potential params from the predefined endpoint
    const params: string[] = [];
    // update with defaults
    (this.structure.defaults || []).forEach((def: IEndpointBuilderDefault) => {
      if ((def.active || def.active === undefined) && def.parameterValue) {
        params.push(def.parameterName + '=' + def.parameterValue);
      }
    });
    // update with chips
    this.structure.titles.forEach((title: IEndpointBuilderTitle) => {
      // --- title
      if (title.visible !== false) {
        title.subtitles.forEach((subtitle: IEndpointBuilderSubtitle) => {
          // --- subtitle
          if (subtitle.visible !== false) {
            subtitle.chips.forEach((chip: IEndpointBuilderChip) => {
              // ---- for each chips ----
              if (
                chip.forceParameter ||
                (chip.selected &&
                  chip.active !== false &&
                  chip.visible !== false &&
                  chip.parameterName &&
                  chip.parameterValue)
              ) {
                // push chip parameter to endpoint
                params.push(chip.parameterName + '=' + chip.parameterValue);
              }
            });
          }
        });
      }
    });
    // update endpoint in parent
    if (this.endpointChangeHandler) {
      if (params.length > 0) {
        this.endpointChangeHandler(endpointBase + '?' + params.join('&'));
      } else {
        this.endpointChangeHandler(endpointBase);
      }
    }
  }

  // extract the params from endpoint and set the chips from them
  parseEndpoint(): void {
    const params: Map<string, string> = this.extractParamsFromURL(this.endpoint);
    this.setChipFromParams(params);
    this.setDefaultFromParams(params);
  }

  // active chip from given key-value param map - if a chip see its param defined in the list, it will activate
  setChipFromParams(params: Map<string, string>): void {
    this.structure.titles.forEach((title: IEndpointBuilderTitle) => {
      title.subtitles.forEach((subtitle: IEndpointBuilderSubtitle) => {
        subtitle.chips.forEach((chip: IEndpointBuilderChip) => {
          // ---- for each chips ----
          if (chip.parameterName) {
            const newParameterValue = params.get(chip.parameterName);
            if (
              newParameterValue &&
              (chip.softParameterValue || chip.parameterValue === newParameterValue)
            ) {
              chip.selected = true; // if chip have parameter defined in params, then it's active
              this.computeAllNodes(true);
            } else {
              chip.selected = false;
              this.computeAllNodes(true);
            }
          }
        });
      });
    });
  }

  // Only overrides the defaults that are setByParams
  setDefaultFromParams(params: Map<string, string>): void {
    if (this.structure.defaults) {
      this.structure.defaults.forEach((defaultVal: IEndpointBuilderDefault) => {
        if (defaultVal.setByParams === true) {
          const newParameterValue = params.get(defaultVal.parameterName);
          if (newParameterValue) {
            defaultVal.active = true;
            this.computeAllNodes(true);
          } else {
            defaultVal.active = false;
            this.computeAllNodes(true);
          }
        }
      });
    }
  }

  // extract params from given URL
  extractParamsFromURL(url: string): Map<string, string> {
    const params: Map<string, string> = new Map();
    // get parameter part of URL (after the '?')
    const endpointParts: string[] = url.split('?');
    if (endpointParts.length < 2) {
      return params; // no parameter detected in endpoint
    }
    // iterate on each parameter
    const paramsStr: string = endpointParts.slice(1).join('');
    const splitParamsStr = paramsStr.split('&');

    for (const paramStr of splitParamsStr) {
      // extract key-value from each parameter
      const valueKey: string[] = paramStr.split('=');
      if (valueKey.length < 2) {
        continue; // not a key-value parameter
      }
      const key: string = valueKey[0];
      const value: string = valueKey.slice(1).join('');
      params.set(key, value);
    }

    return params;
  }
}
